import * as React from 'react';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';

import styles from './ReviewSummary.module.css';

export interface ReviewItem {
  label: string;
  value: React.ReactNode;
}

export interface ReviewSection {
  id: string;
  title: string;
  items: ReviewItem[];
}

export interface ReviewSummaryProps {
  sections: ReviewSection[];
  onEdit?: (sectionId: string) => void;
}

const isEmpty = (value: React.ReactNode) =>
  value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

/** Read-only recap of every step, each with a shortcut back to edit it. */
const ReviewSummary: React.FC<ReviewSummaryProps> = ({ sections, onEdit }) => (
  <Box className={styles.grid}>
    {sections.map((section) => (
      <Card key={section.id} variant="outlined" component="section" aria-labelledby={`review-${section.id}`}>
        <CardContent>
          <Box className={styles.header}>
            <Typography id={`review-${section.id}`} variant="subtitle1" component="h3">
              {section.title}
            </Typography>
            {onEdit && (
              <Button
                size="small"
                startIcon={<EditOutlinedIcon fontSize="small" />}
                onClick={() => onEdit(section.id)}
                aria-label={`Edit ${section.title}`}
              >
                Edit
              </Button>
            )}
          </Box>
          <dl className={styles.list}>
            {section.items.map((item) => (
              <React.Fragment key={item.label}>
                <dt className={styles.term}>{item.label}</dt>
                <dd className={styles.value}>
                  {isEmpty(item.value) ? (
                    <Typography variant="body2" color="text.secondary" component="span">
                      —
                    </Typography>
                  ) : (
                    item.value
                  )}
                </dd>
              </React.Fragment>
            ))}
          </dl>
        </CardContent>
      </Card>
    ))}
  </Box>
);

export default ReviewSummary;
